// Owner analytics — tenant-scoped trends over the same daily rollups the
// superadmin sees, redesigned for an owner on their phone: preset ranges,
// tappable metric cards with vs-prior-period deltas, and a smooth line chart
// instead of the admin's bar strip.

import { useMemo, useState } from 'react'

import { OwnerApiError } from '../ownerApi.js'
import {
  useOwnerAnalytics,
  type AnalyticsMetric,
} from '../hooks/useOwnerAnalytics.js'
import { TrendChart } from '../../shared/trend-chart/TrendChart.js'
import './analyticsPage.css'

const PRESETS = [
  { key: '7', label: '7 days' },
  { key: '30', label: '30 days' },
  { key: '90', label: '90 days' },
  { key: 'this_month', label: 'This month' },
  { key: 'last_month', label: 'Last month' },
  { key: 'all_time', label: 'All time' },
] as const

type PresetKey = (typeof PRESETS)[number]['key']

const METRICS: Array<{
  key: AnalyticsMetric
  label: string
  unit: string
  /** Replaces the delta line for snapshot metrics. */
  sub?: string
}> = [
  { key: 'stamps_issued', label: 'Stamps given', unit: 'stamps' },
  { key: 'passes_installed', label: 'New passes', unit: 'passes' },
  { key: 'rewards_redeemed', label: 'Rewards redeemed', unit: 'rewards' },
  { key: 'cycle_completions', label: 'Cards completed', unit: 'cards' },
  { key: 'broadcasts_sent', label: 'Broadcasts sent', unit: 'broadcasts' },
  {
    key: 'active_customers',
    label: 'Active customers',
    unit: 'customers',
    sub: 'stamped in the last 30 days',
  },
]

function phDate(offsetDays = 0): string {
  return new Date(Date.now() + 8 * 3_600_000 - offsetDays * 86_400_000)
    .toISOString()
    .slice(0, 10)
}

// PH-local calendar-month boundaries. monthOffset 0 = current month (ends
// today, not the 30th/31st — the month is still "in progress"); -1 = the
// full previous month.
function monthRange(monthOffset: number): { from: string; to: string } {
  const ph = new Date(Date.now() + 8 * 3_600_000)
  const y = ph.getUTCFullYear()
  const m = ph.getUTCMonth()
  const total = y * 12 + m + monthOffset
  const year = Math.floor(total / 12)
  const month = ((total % 12) + 12) % 12
  const fmt = (d: Date) => d.toISOString().slice(0, 10)
  const first = fmt(new Date(Date.UTC(year, month, 1)))
  const to =
    monthOffset === 0
      ? phDate()
      : fmt(new Date(Date.UTC(year, month + 1, 0))) // day 0 of next month
  return { from: first, to }
}

export function AnalyticsPage() {
  const [preset, setPreset] = useState<PresetKey>('30')
  const [metric, setMetric] = useState<AnalyticsMetric>('stamps_issued')
  const [showRedemptionInfo, setShowRedemptionInfo] = useState(false)
  const [showRepeatInfo, setShowRepeatInfo] = useState(false)

  const range = useMemo(() => {
    if (preset === 'this_month') return monthRange(0)
    if (preset === 'last_month') return monthRange(-1)
    // 'all' is a sentinel the backend resolves to the tenant's actual
    // creation date — see routes/owner/analytics.ts resolveRange. The real
    // from-date comes back in the response (q.data.from below).
    if (preset === 'all_time') return { from: 'all', to: phDate() }
    return { from: phDate(Number(preset) - 1), to: phDate() }
  }, [preset])
  const q = useOwnerAnalytics(range.from, range.to)
  // Resolved from/to for display + the delta comparison span — once loaded,
  // prefer the server's echoed values (real dates even for 'all').
  const displayFrom = q.data?.from ?? (range.from === 'all' ? range.to : range.from)
  const displayTo = q.data?.to ?? range.to
  const days = useMemo(() => {
    const fromMs = new Date(`${displayFrom}T00:00:00+08:00`).getTime()
    const toMs = new Date(`${displayTo}T00:00:00+08:00`).getTime()
    return Math.round((toMs - fromMs) / 86_400_000) + 1
  }, [displayFrom, displayTo])

  const activeMetric = METRICS.find((m) => m.key === metric)!
  const chartData =
    q.data?.daily.map((d) => ({ date: d.date, value: d[metric] })) ?? []
  const hasAnyActivity = chartData.some((d) => d.value > 0)

  return (
    <div className="oa-page">
      <h1>Analytics</h1>
      <p className="oa-sub">
        How your loyalty program is doing. Today's numbers are included as
        they happen.
      </p>

      <div className="oa-presets" role="tablist" aria-label="Date range">
        {PRESETS.map((p) => (
          <button
            key={p.key}
            type="button"
            role="tab"
            aria-selected={preset === p.key}
            className={`oa-preset${preset === p.key ? ' is-active' : ''}`}
            onClick={() => setPreset(p.key)}
          >
            {p.label}
          </button>
        ))}
      </div>

      {q.isLoading && (
        <div className="oa-cards" aria-hidden="true">
          {METRICS.map((m) => (
            <div key={m.key} className="oa-card oa-card-skeleton" />
          ))}
        </div>
      )}

      {q.isError && (
        <div className="owner-card owner-empty-state">
          <div className="owner-card-title">Couldn't load analytics</div>
          <p className="owner-card-sub">
            {q.error instanceof OwnerApiError
              ? q.error.userMessage
              : 'Something went wrong. Please try again.'}
          </p>
          <button
            type="button"
            className="owner-btn owner-btn-primary owner-btn-sm"
            onClick={() => q.refetch()}
          >
            Try again
          </button>
        </div>
      )}

      {q.data && (
        <>
          {/* Metric cards double as the chart's metric selector — tap a card
              to plot it. Bigger touch targets than a dropdown and the active
              selection stays visible. */}
          <div className="oa-cards">
            {METRICS.map((m) => {
              const value = q.data.totals[m.key]
              const delta = m.sub
                ? null
                : formatDelta(value, q.data.previous_totals[m.key], days)
              return (
                <button
                  key={m.key}
                  type="button"
                  className={`oa-card${metric === m.key ? ' is-active' : ''}`}
                  aria-pressed={metric === m.key}
                  onClick={() => setMetric(m.key)}
                >
                  <span className="oa-card-label">{m.label}</span>
                  <span className="oa-card-value">{value.toLocaleString()}</span>
                  {delta ? (
                    <span className={`oa-card-delta oa-delta-${delta.tone}`}>
                      {delta.arrow && (
                        <span aria-hidden="true">{delta.arrow} </span>
                      )}
                      {delta.text}
                    </span>
                  ) : (
                    <span className="oa-card-delta oa-delta-flat">{m.sub}</span>
                  )}
                </button>
              )
            })}
          </div>

          {/* Program health — derived ratios, not additive counts, so they
              live in their own section (not the chart-tappable grid above)
              with a short tap-to-reveal explanation of the calculation. */}
          <h2 className="oa-section-heading">Program health</h2>
          <div className="oa-cards oa-cards-secondary">
            {(() => {
              const rate = ratioOrNull(
                q.data.totals.rewards_redeemed,
                q.data.totals.milestones_earned,
              )
              const prevRate = ratioOrNull(
                q.data.previous_totals.rewards_redeemed,
                q.data.previous_totals.milestones_earned,
              )
              const delta = formatPointDelta(rate, prevRate)
              return (
                <div className="oa-card oa-card-static">
                  <span className="oa-card-label-row">
                    <span className="oa-card-label">Redemption rate</span>
                    <button
                      type="button"
                      className="oa-info-btn"
                      aria-expanded={showRedemptionInfo}
                      aria-label="How is redemption rate calculated?"
                      onClick={() => setShowRedemptionInfo((v) => !v)}
                    >
                      ⓘ
                    </button>
                  </span>
                  <span className="oa-card-value">{formatPercent(rate)}</span>
                  {delta ? (
                    <span className={`oa-card-delta oa-delta-${delta.tone}`}>
                      {delta.arrow && (
                        <span aria-hidden="true">{delta.arrow} </span>
                      )}
                      {delta.text}
                    </span>
                  ) : (
                    <span className="oa-card-delta oa-delta-flat">
                      of earned rewards claimed
                    </span>
                  )}
                  {showRedemptionInfo && (
                    <p className="oa-card-explain">
                      Rewards claimed ÷ rewards earned. A reward is "earned"
                      the moment a stamp reaches one of your program's
                      milestones — this tracks how many of those actually get
                      redeemed.
                    </p>
                  )}
                </div>
              )
            })()}

            <div className="oa-card oa-card-static">
              <span className="oa-card-label-row">
                <span className="oa-card-label">Repeat customers</span>
                <button
                  type="button"
                  className="oa-info-btn"
                  aria-expanded={showRepeatInfo}
                  aria-label="How is repeat customers calculated?"
                  onClick={() => setShowRepeatInfo((v) => !v)}
                >
                  ⓘ
                </button>
              </span>
              <span className="oa-card-value">
                {formatPercent(q.data.return_visit.rate)}
              </span>
              <span className="oa-card-delta oa-delta-flat">
                {q.data.return_visit.total_customers > 0
                  ? `${q.data.return_visit.returning_customers} of ${q.data.return_visit.total_customers} stamped 2+ days`
                  : 'No stamps in this range yet'}
              </span>
              {showRepeatInfo && (
                <p className="oa-card-explain">
                  % of customers who stamped on 2+ different days in this
                  range — a one-time visit doesn't count as a repeat, however
                  many stamps they got that day.
                </p>
              )}
            </div>
          </div>

          <div className="owner-card oa-chart-card">
            <div className="oa-chart-head">
              <h2>{activeMetric.label}</h2>
              <span className="oa-chart-range">
                {formatRange(displayFrom, displayTo)}
              </span>
            </div>
            <TrendChart data={chartData} unit={activeMetric.unit} />
            {!hasAnyActivity && (
              <p className="oa-chart-empty">
                No {activeMetric.unit} recorded in this range yet.
              </p>
            )}
          </div>

          {preset !== 'all_time' && (
            <p className="oa-footnote">
              Compared against the {days} days before this range.
            </p>
          )}
        </>
      )}
    </div>
  )
}

interface Delta {
  text: string
  tone: 'up' | 'down' | 'flat' | 'new'
  arrow: string | null
}

/** "+18% vs prior 30d" with an arrow — same math as the admin stat cards. */
function formatDelta(
  value: number,
  prev: number,
  spanDays: number,
): Delta | null {
  const suffix = `vs prior ${spanDays}d`
  if (prev === 0 && value === 0) {
    return { text: `— ${suffix}`, tone: 'flat', arrow: null }
  }
  if (prev === 0 && value > 0) {
    return { text: `new ${suffix}`, tone: 'new', arrow: '↗' }
  }
  const pct = ((value - prev) / prev) * 100
  if (Math.abs(pct) < 0.05) {
    return { text: `±0% ${suffix}`, tone: 'flat', arrow: null }
  }
  const rounded =
    pct >= 10 || pct <= -10 ? Math.round(pct) : Math.round(pct * 10) / 10
  return {
    text: `${pct > 0 ? '+' : ''}${rounded}% ${suffix}`,
    tone: pct > 0 ? 'up' : 'down',
    arrow: pct > 0 ? '↗' : '↘',
  }
}

/** null when the denominator is 0 — "no data" rather than a misleading 0%. */
function ratioOrNull(numerator: number, denominator: number): number | null {
  return denominator > 0 ? numerator / denominator : null
}

function formatPercent(rate: number | null): string {
  return rate === null ? '—' : `${Math.round(rate * 100)}%`
}

/** Percentage-POINT delta ("+6pp vs prior"), not a relative %-change —
 *  reusing formatDelta's relative-% math on an already-percentage value
 *  would misleadingly amplify small rate shifts (40% -> 46% is "+6pp", not
 *  the relative "+15%" formatDelta would report). */
function formatPointDelta(rate: number | null, prev: number | null): Delta | null {
  if (rate === null || prev === null) return null
  const diff = Math.round((rate - prev) * 100)
  if (diff === 0) return { text: '±0pp vs prior', tone: 'flat', arrow: null }
  return {
    text: `${diff > 0 ? '+' : ''}${diff}pp vs prior`,
    tone: diff > 0 ? 'up' : 'down',
    arrow: diff > 0 ? '↗' : '↘',
  }
}

function formatRange(from: string, to: string): string {
  const day = (iso: string) => {
    const months = [
      'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
      'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
    ]
    const [, m, d] = iso.split('-')
    return `${months[Number(m) - 1] ?? m} ${Number(d)}`
  }
  return `${day(from)} – ${day(to)}`
}
